import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { tickets as ticketsApi } from '../services/api';
import { subscribeTicketUpdates } from '../services/sse';
import { useProject } from './ProjectContext';

const AgentContext = createContext();

export const AGENTS = [
  { id: 'enhance', label: 'Enhance', icon: '✦', color: '#8b5cf6' },
  { id: 'plan', label: 'Plan', icon: '◎', color: '#3b82f6' },
  { id: 'implement', label: 'Implement', icon: '⚡', color: '#f59e0b' },
  { id: 'review', label: 'Review', icon: '✓', color: '#10b981' },
];

export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) throw new Error('useAgent must be used within an AgentProvider');
  return context;
};

export const getActiveAgent = (task) => {
  if (!task?.pipeline) return null;
  for (const agent of AGENTS) {
    if (task.pipeline[agent.id]?.status === 'running') return agent;
  }
  return null;
};

export const isTaskActive = (task) => {
  return getActiveAgent(task) !== null;
};

export const getCompletedCount = (task) => {
  if (!task?.pipeline) return 0;
  return AGENTS.filter(a => task.pipeline[a.id]?.status === 'completed').length;
};

export const getNextPendingAgent = (task) => {
  if (!task?.pipeline) return null;
  for (const agent of AGENTS) {
    const status = task.pipeline[agent.id]?.status;
    if (status === 'pending') {
      // Skip enhance if plan has already progressed
      if (agent.id === 'enhance') {
        const planStatus = task.pipeline.plan?.status;
        if (planStatus && planStatus !== 'pending') continue;
      }
      // Skip review if no PR URL exists
      if (agent.id === 'review') {
        const prUrl = task.pipeline.review?.pr_url || task.job?.pr_url;
        if (!prUrl) return null;
      }
      return agent;
    }
    if (status === 'running' || status === 'failed') return null;
  }
  return null;
};

export const AgentProvider = ({ children }) => {
  const { selectedProject } = useProject();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadTasks = useCallback(async () => {
    if (!selectedProject?.id) return;
    try {
      setLoading(true);
      const response = await ticketsApi.getAllForProject(selectedProject.id);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProject?.id]);

  const refreshTasks = useCallback(async () => {
    if (!selectedProject?.id) return;
    try {
      const response = await ticketsApi.getAllForProject(selectedProject.id);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to refresh tasks:', error);
    }
  }, [selectedProject?.id]);

  // Refresh a single task from the API and update it in the array
  const refreshTask = useCallback(async (taskId) => {
    try {
      const response = await ticketsApi.getOne(taskId);
      const updated = response.data;
      setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
      return updated;
    } catch (error) {
      console.error('Failed to refresh task:', error);
      return null;
    }
  }, []);

  // Load tasks when project changes
  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // SSE: subscribe to real-time ticket updates via shared connection
  useEffect(() => {
    return subscribeTicketUpdates((updated) => {
      setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
    });
  }, [selectedProject?.id]);

  const value = {
    tasks,
    loading,
    loadTasks,
    refreshTasks,
    refreshTask,
  };

  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
};

export default AgentContext;
